import {
  IMetaExpressionOptions,
  resolveRawMetaExpressionInString,
} from "fieldpro-tools";

import { IClient } from "model/entities/Client";
import {
  FieldTypes,
  getFieldsByType,
} from "utils/metaExpressions/fieldOptions";

export type SqlTokenType =
  | "meta"
  | "comment"
  | "string"
  | "number"
  | "kw"
  | "fn"
  | "id"
  | "op"
  | "ws";

export interface SqlToken {
  t: string;
  type: SqlTokenType;
  start: number;
  end: number;
}

const KEYWORDS = new Set([
  "SELECT",
  "FROM",
  "WHERE",
  "AS",
  "CASE",
  "WHEN",
  "THEN",
  "ELSE",
  "END",
  "AND",
  "OR",
  "NOT",
  "NULL",
  "IS",
  "TRUE",
  "FALSE",
  "GROUP",
  "BY",
  "ORDER",
  "ON",
  "JOIN",
  "LEFT",
  "RIGHT",
  "INNER",
  "OUTER",
  "HAVING",
  "DISTINCT",
  "IN",
  "LIKE",
  "BETWEEN",
  "LIMIT",
  "ASC",
  "DESC",
  "UNION",
  "ALL",
]);

const FUNCTIONS = new Set([
  "SUM",
  "COUNT",
  "ROUND",
  "CONCAT",
  "AVG",
  "MIN",
  "MAX",
  "COALESCE",
  "CAST",
  "DATE",
  "NOW",
  "LOWER",
  "UPPER",
  "TRIM",
  "LENGTH",
  "ABS",
  "FLOOR",
  "CEIL",
]);

/** Matches stored meta-expression formats (same as InputMetaExpressionField / getFormattedDefaultValue). */
export const META_EXPRESSION_TOKEN_PATTERN =
  /\$\$[^$]+\$\$|\$\^[\s\S]*?\^|'(\$\{[^}]*\})'|\$\{[^}]*\}/g;

const META_TOKEN_PATTERNS = [
  /^\$\$[^$]+\$\$/,
  /^\$\^[\s\S]*?\^/,
  /^'(\$\{[^}]*\})'/,
  /^\$\{[^}]*\}/,
];

const COMMENT_PATTERN = /^--[^\n]*/;
const STRING_PATTERN = /^'(?:[^']|'')*'/;
const NUMBER_PATTERN = /^\d+(?:\.\d+)?/;
const IDENTIFIER_PATTERN = /^[A-Za-z_]\w*/;
const WHITESPACE_PATTERN = /^\s+/;
const OPERATOR_PATTERN = /^[^\s\w]/;

function classifyIdentifier(id: string): SqlTokenType {
  const upper = id?.toUpperCase() ?? "";
  if (KEYWORDS.has(upper)) {
    return "kw";
  }
  if (FUNCTIONS.has(upper)) {
    return "fn";
  }
  return "id";
}

function matchNextToken(code: string, pos: number): SqlToken {
  const remaining = code?.slice(pos) ?? "";

  for (const pattern of META_TOKEN_PATTERNS) {
    const match = pattern.exec(remaining);
    if (match) {
      const t = match[0];
      return { t, type: "meta", start: pos, end: pos + t.length };
    }
  }

  const simpleMatchers: Array<{ pattern: RegExp; type: SqlTokenType }> = [
    { pattern: COMMENT_PATTERN, type: "comment" },
    { pattern: STRING_PATTERN, type: "string" },
    { pattern: NUMBER_PATTERN, type: "number" },
    { pattern: WHITESPACE_PATTERN, type: "ws" },
    { pattern: OPERATOR_PATTERN, type: "op" },
  ];

  for (const { pattern, type } of simpleMatchers) {
    const match = pattern.exec(remaining);
    if (match) {
      const t = match[0];
      return { t, type, start: pos, end: pos + t.length };
    }
  }

  const idMatch = IDENTIFIER_PATTERN.exec(remaining);
  if (idMatch) {
    const t = idMatch[0];
    return { t, type: classifyIdentifier(t), start: pos, end: pos + t.length };
  }

  return { t: code?.[pos] ?? "", type: "op", start: pos, end: pos + 1 };
}

export function tokenize(code: string): SqlToken[] {
  const tokens: SqlToken[] = [];
  let pos = 0;

  while (pos < (code?.length ?? 0)) {
    const token = matchNextToken(code ?? "", pos);
    tokens.push(token);
    pos = token.end;
  }

  return tokens;
}

/**
 * Collapses formatting whitespace in a SQL expression while preserving
 * content inside string literals and single-line comments unchanged.
 * After a -- comment a newline is preserved so the comment stays
 * terminated and does not consume subsequent tokens.
 */
export function normalizeSqlWhitespace(expression: string): string {
  const tokens = tokenize(expression);
  const parts: string[] = [];
  let pendingSpace = false;
  let prevNonWsType = "";

  for (const token of tokens) {
    if (token.type === "ws") {
      if (prevNonWsType === "comment") {
        // After a -- comment the first newline must be preserved so the
        // comment stays terminated and does not consume the next token.
        if (token.t.includes("\n")) {
          parts.push("\n");
        }
        pendingSpace = false;
      } else {
        pendingSpace = true;
      }
    } else {
      if (pendingSpace && parts.length > 0) {
        parts.push(" ");
      }
      parts.push(token.t);
      pendingSpace = false;
      prevNonWsType = token.type;
    }
  }

  return parts.join("").trim();
}

export function clampInsertPosition(
  pos: number,
  tokens: Array<{ start: number; end: number }>,
): number {
  const inside = tokens.find((token) => pos > token.start && pos < token.end);
  if (!inside) {
    return pos;
  }

  return pos - inside.start <= inside.end - pos ? inside.start : inside.end;
}

export function normalizeMetaExpressionsForSqlQuery(query: string): string {
  return (
    query?.replace(
      /\$\^(__active_[\s\S]*?)\^/g,
      (_, expression) => `\${${expression}}`,
    ) ?? ""
  );
}

function buildActiveClientFieldExpressions(): string[] {
  return getFieldsByType(FieldTypes.CLIENT_FIELDS).flatMap((fieldId) => {
    const expression = `__active_client/__field:${fieldId}`;
    if (fieldId.startsWith("_")) {
      return [expression, `__active_client/__field:${fieldId.slice(1)}`];
    }
    return [expression];
  });
}

/** Catalog MEs plus active-scope expressions used in transformation SQL (not stored in catalog). */
export function getMetaExpressionsForTransformationQueryRestore(
  catalogExpressions: string[],
): string[] {
  return [
    ...new Set([...catalogExpressions, ...buildActiveClientFieldExpressions()]),
  ];
}

/** Resolves active client field MEs locally (no API) for fetch-time token restore. */
export function resolveActiveClientFieldMetaExpressions(
  client: IClient | null | undefined,
): Record<string, string> {
  if (!client) {
    return {};
  }

  const options: IMetaExpressionOptions = { client };
  const resolved: Record<string, string> = {};

  for (const expression of buildActiveClientFieldExpressions()) {
    const value = resolveRawMetaExpressionInString(expression, options);
    if (value != null && value !== "") {
      resolved[expression] = String(value);
    }
  }

  return resolved;
}

/** Replaces SQL-quoted resolved ME values with $^...^ tokens when loading from backend. */
export function restoreMetaTokensInTransformationQuery(
  query: string,
  resolved: Record<string, string>,
): string {
  if (extractBareMetaExpressionsFromSqlQuery(query).length > 0) {
    return query;
  }

  let restored = query ?? "";
  const replacements = Object.entries(resolved)
    .filter(([, value]) => value)
    .map(([expression, value]) => ({
      literal: toSqlStringLiteral(value),
      token: `$^${expression}^`,
    }))
    .sort((a, b) => b.literal.length - a.literal.length);

  for (const { literal, token } of replacements) {
    if ((restored ?? "").includes(literal)) {
      restored = (restored ?? "").split(literal).join(token);
    }
  }

  return restored;
}

export function toSqlStringLiteral(value: string): string {
  return `'${value?.replace(/'/g, "''") ?? ""}'`;
}

/** Replaces ME tokens with SQL-quoted resolved values for backend validation. */
export function substituteMetaExpressionsInSqlQuery(
  query: string,
  resolved: Record<string, string>,
): string {
  let result = query ?? "";

  result = (result ?? "").replace(
    /\$\^([\s\S]*?)\^/g,
    (_, expression: string) => toSqlStringLiteral(resolved[expression] ?? ""),
  );

  result = (result ?? "").replace(
    /'(\$\{([^}]*)\})'/g,
    (_match, _token: string, expression: string) =>
      toSqlStringLiteral(resolved[expression] ?? ""),
  );

  result = (result ?? "").replace(/\$\{([^}]*)\}/g, (_, expression: string) =>
    toSqlStringLiteral(resolved[expression] ?? ""),
  );

  return result;
}

/** Extracts bare meta-expression strings from stored SQL query tokens. */
export function extractBareMetaExpressionsFromSqlQuery(
  query: string,
): string[] {
  const expressions: string[] = [];
  let match: RegExpExecArray | null;

  const caretRe = /\$\^([\s\S]*?)\^/g;
  while ((match = caretRe.exec(query)) !== null) {
    expressions.push(match[1]);
  }

  const braceRe = /\$\{([^}]*)\}/g;
  while ((match = braceRe.exec(query)) !== null) {
    expressions.push(match[1]);
  }

  return [...new Set(expressions)];
}

export function splitTopLevel(str: string): string[] {
  if (!str) return [];

  const parts: string[] = [];
  let depth = 0;
  let current = "";
  let inString = false;
  let i = 0;

  while (i < (str?.length ?? 0)) {
    const char = str?.[i] ?? "";
    if (char === "'") {
      if (inString && str?.[i + 1] === "'") {
        current += "''";
        i += 2;
        continue;
      }
      inString = !inString;
    }
    if (!inString && char === "(") {
      depth++;
    }
    if (!inString && char === ")") {
      depth--;
    }
    if (!inString && char === "," && depth === 0) {
      parts.push(current);
      current = "";
    } else {
      current += char;
    }
    i++;
  }

  if (current?.trim()) {
    parts.push(current);
  }

  return parts;
}

function normalize(sql: string): string {
  const tokens = tokenize(sql).filter(
    (token) => token.type !== "ws" && token.type !== "comment",
  );
  let output = "";

  for (let i = 0; i < tokens.length; i++) {
    const current = tokens[i]?.t ?? "";
    const previousToken = tokens[i - 1];
    const previous = previousToken ? previousToken.t : "";
    const noSpaceBefore = [",", ")", "."].includes(current);
    const previousNoSpaceAfter = ["(", "."].includes(previous);
    const functionParen =
      current === "(" &&
      previousToken &&
      (previousToken.type === "fn" || previousToken.type === "id");

    output +=
      i === 0 || noSpaceBefore || previousNoSpaceAfter || functionParen
        ? current
        : ` ${current}`;
  }

  return output;
}

export function formatSQL(sql: string): string {
  let formatted = normalize(sql);

  [
    "SELECT",
    "FROM",
    "WHERE",
    "GROUP BY",
    "ORDER BY",
    "HAVING",
    "LIMIT",
  ].forEach((clause) => {
    formatted = formatted.replace(
      new RegExp(`\\b${clause.replace(" ", "\\s+")}\\b`, "ig"),
      clause,
    );
  });

  if (!/^\s*SELECT\b/i.test(formatted)) {
    return formatted;
  }

  const afterSelect = formatted.slice(formatted.indexOf("SELECT") + 6);
  const fromMatchIndex = afterSelect.search(/\bFROM\b/);

  let columns: string;
  let rest = "";

  if (fromMatchIndex < 0) {
    columns = afterSelect;
  } else {
    columns = afterSelect.slice(0, fromMatchIndex);
    rest = afterSelect?.slice(fromMatchIndex) ?? "";
  }

  const columnLines = splitTopLevel(columns)
    .map((column) => `  ${column.trim()}`)
    .join(",\n");

  rest = (rest ?? "")
    .replace(/\bFROM\b/i, "\nFROM")
    .replace(/\bWHERE\b/i, "\nWHERE")
    .replace(/\bGROUP BY\b/i, "\nGROUP BY")
    .replace(/\bORDER BY\b/i, "\nORDER BY")
    .replace(/\bHAVING\b/i, "\nHAVING")
    .replace(/\bLIMIT\b/i, "\nLIMIT")
    .trim();

  const restSuffix = rest ? `\n${rest}` : "";
  return `SELECT\n${columnLines}${restSuffix}`;
}
